import React, { FC, ReactElement } from 'react';
import classnames from 'classnames';

import AlertPersistent from 'buyside/components/presentation/AlertPersistent/alertPersistent';
import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import RequestAccessButton from 'buyside/components/presentation/RequestAccessButton/requestAccessButton';
import SubmitResearchButton from 'buyside/components/presentation/Button/SubmitResearchButton/submitResearchButton';

import { accessIdeasUrl } from 'buyside/util/urlHelper';

import { Maybe, useMainNavigationCurrentUserQuery } from 'interfaces/graphql-typings';

import styles from './redactedStyles.scss';

export enum Location {
  corporateActionFeed = 'corporate-action-feed',
  entityFeed = 'entity-feed',
  entityRelatedCos = 'entity-related-companies',
  fundResearch = 'fund-research',
  ideaComments = 'idea-comments',
  ideaRelatedIdeas = 'idea-related-ideas',
  ideasIndex = 'ideas-index',
  ideaUpdates = 'idea-updates',
  profileResearch = 'profile-research',
  postRelatedIdeas = 'post-related-ideas',
  priceChart = 'price-chart',
  publicUserIdeas = 'public-user-ideas',
  whitePaperRelatedIdeas = 'white-paper-related-ideas'
}

const DelayedAccessAlert: FC<{
  id: Location;
  isPublic?: boolean;
  sectionName?: string;
  customStyles?: string;
  userId?: string;
}> = ({ id, customStyles, isPublic, sectionName, userId }): Maybe<ReactElement> => {
  const { data } = useMainNavigationCurrentUserQuery({ skip: isPublic });

  if (isPublic)
    return (
      <AlertPersistent customStyles={styles.alert} name={`delay-90-${id}`}>
        You're viewing the {sectionName || 'research library'} with a 90-day delay. Become a member
        to access more recent data.
      </AlertPersistent>
    );

  const canViewIdeas = data?.currentUser && data.currentUser?.canViewIdeas;
  if (canViewIdeas) return null;

  if (!data?.currentUser) return null;

  const { id: currentUserId, canWriteIdeas, isAllocator } = data.currentUser;

  if (isAllocator) {
    return id === Location.fundResearch ? null : (
      <AlertPersistent
        customStyles={classnames(customStyles, styles.alert)}
        name={`delay-45-${id}`}
      >
        As an allocator, you have real-time/full access to research featured by cap intro funds.
        Otherwise, you're viewing the {sectionName || 'research library'} with a 45-day delay. Get
        in touch with us about upgrading your account.
        <RequestAccessButton
          size={BUTTON_SIZES.TINIEST}
          text="Contact Us"
          type={BUTTON_TYPES.PRIMARY}
        />
      </AlertPersistent>
    );
  }

  if ([Location.profileResearch, Location.publicUserIdeas].includes(id) && userId === currentUserId)
    return null;

  const upsellMessage = `You're viewing the ${sectionName ||
    'research library'} with a 45-day delay. Get real-time access by `;

  return (
    <AlertPersistent customStyles={classnames(customStyles, styles.alert)} name={`delay-45-${id}`}>
      {upsellMessage}
      {canWriteIdeas ? 'sharing your own research or ' : ''}subscribing.
      <div
        className={classnames({
          [styles.buttons]: [
            Location.entityFeed,
            Location.entityRelatedCos,
            Location.priceChart
          ].includes(id)
        })}
      >
        {canWriteIdeas && (
          <SubmitResearchButton className={styles.button} icon={null} size={BUTTON_SIZES.TINIEST} />
        )}
        <Button
          className={styles.button}
          text="Subscribe"
          type={BUTTON_TYPES.MAJOR}
          size={BUTTON_SIZES.TINIEST}
          url={accessIdeasUrl()}
        />
      </div>
    </AlertPersistent>
  );
};

export default DelayedAccessAlert;
